import { getConfig } from "config";
import { fingerprintCustomerScan } from "services/unresolvedCustomerDeposits";

export const CUSTOMER_DEPOSIT_RETRY_TTL_MS = 24 * 60 * 60 * 1000;
type RetryRecord = { idempotencyKey: string; expiresAt: number };
const key = (scan: string) => `customer-deposit-retry:${getConfig().lockerId}:${scan}`;
const validKey = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9._:-]{1,255}$/.test(value);

function writeRecord(scan: string, record: RetryRecord) {
  const serialized = JSON.stringify(record);
  localStorage.setItem(key(scan), serialized);
  if (localStorage.getItem(key(scan)) !== serialized)
    throw new Error("Deposit retry identity unavailable");
}

function readRecord(scan: string): RetryRecord | null {
  const raw = localStorage.getItem(key(scan));
  if (raw === null) return null;
  let record: RetryRecord;
  try {
    record = JSON.parse(raw);
  } catch {
    throw new Error("Deposit retry identity unavailable");
  }
  if (
    !record ||
    !validKey(record.idempotencyKey) ||
    !Number.isSafeInteger(record.expiresAt) ||
    record.expiresAt <= 0
  )
    throw new Error("Deposit retry identity unavailable");
  if (record.expiresAt <= Date.now()) {
    // Expiry removes only replay identity. Uncertain-door evidence is separate.
    if (!clearCustomerDepositRetry(scan)) throw new Error("Deposit retry expiry unavailable");
    return null;
  }
  return record;
}

export function readCustomerDepositRetry(scan: string): string | null {
  return readRecord(scan)?.idempotencyKey ?? null;
}

export function saveCustomerDepositRetry(scan: string, idempotencyKey: string) {
  const existing = readRecord(scan);
  if (existing && existing.idempotencyKey !== idempotencyKey)
    throw new Error("Deposit retry identity changed");
  if (!validKey(idempotencyKey)) throw new Error("Deposit retry identity unavailable");
  // Home, reload and retries preserve the original deadline rather than sliding it.
  writeRecord(
    scan,
    existing ?? {
      idempotencyKey,
      expiresAt: Date.now() + CUSTOMER_DEPOSIT_RETRY_TTL_MS,
    }
  );
}

export function clearCustomerDepositRetry(scan: string) {
  try {
    localStorage.removeItem(key(scan));
    return localStorage.getItem(key(scan)) === null;
  } catch {
    // The caller must keep the door claim if this receipt can still be replayed.
    return false;
  }
}

export async function readCustomerDepositRetryForScan(scan: string) {
  return readCustomerDepositRetry(await fingerprintCustomerScan(scan));
}

export async function assertNoCustomerDepositRetry(reservation: Reservation) {
  if (
    reservation.deposit &&
    reservation.tracking_number &&
    (await readCustomerDepositRetryForScan(reservation.tracking_number))
  )
    throw new Error("Resume the existing customer deposit attempt");
}
